import DB_PATH from "../DB_PATH";
import { firestore } from "../firebase";

export type RemovePlanResult = {
  planDeleted: boolean;
  dayPlansDeleted: number;
  stepsDeleted: number;
  actionsDeleted: number;
};

// Cascade-delete a Plan plus its DayPlans, DailySteps, and any Actions
// referenced by those steps via workoutActionId.
//
// Library Subactions (under HABIT_SUBACTION) are intentionally left alone:
// they are shared infrastructure dedup'd across many plans, and removing one
// would break other plans that reuse the same exercise.
const removePlan = async ({
  planID,
}: {
  planID: string;
}): Promise<RemovePlanResult> => {
  const result: RemovePlanResult = {
    planDeleted: false,
    dayPlansDeleted: 0,
    stepsDeleted: 0,
    actionsDeleted: 0,
  };

  if (!planID) {
    console.error("removePlan: missing planID");
    return result;
  }

  try {
    const stepsSnap = await firestore
      .collection(DB_PATH.PLAN_STEP)
      .where("planID", "==", planID)
      .get();

    const actionIds = new Set<string>();
    for (const doc of stepsSnap.docs) {
      const data = doc.data() as { workoutActionId?: string };
      if (data.workoutActionId) actionIds.add(data.workoutActionId);
    }

    await Promise.all(stepsSnap.docs.map((doc) => doc.ref.delete()));
    result.stepsDeleted = stepsSnap.docs.length;

    const dayPlansSnap = await firestore
      .collection(DB_PATH.PLAN_DAY)
      .where("planID", "==", planID)
      .get();
    await Promise.all(dayPlansSnap.docs.map((doc) => doc.ref.delete()));
    result.dayPlansDeleted = dayPlansSnap.docs.length;

    if (actionIds.size > 0) {
      await Promise.all(
        Array.from(actionIds).map((id) =>
          firestore.collection(DB_PATH.HABIT_ACTION).doc(id).delete()
        )
      );
      result.actionsDeleted = actionIds.size;
    }

    await firestore.collection(DB_PATH.PLAN).doc(planID).delete();
    result.planDeleted = true;

    console.log(
      `removePlan: deleted plan ${planID} (${result.dayPlansDeleted} day-plans, ${result.stepsDeleted} steps, ${result.actionsDeleted} actions)`
    );
  } catch (error) {
    console.error("removePlan: error during cascade delete", error);
  }

  return result;
};

export default removePlan;
